import moment from 'moment';
import Vue from 'vue';
import { getAll, graphQL } from '../../api';
import { getAllScreenItems } from '../../api/modules/screenStats';
import { setFilterOptions } from '@/_store/utils/screens/setFilterOptions';
import { callString, filterParams } from '@/_store/utils/screens/requestConfig';
import { createPaginatedRequest } from '@/_store/utils/app/paginatedRequestBuilder';
import { stringyObject } from '@/_store/utils/helpers';
import router from '@/router';

// state
const state = {
  allScreens: [],
  loadingStates: {
    screensLoaded: true,
  },
  tokens: [undefined],
  currentTokenIndex: 1,
  newScreen: false,
  editScreen: false,
  createdScreen: null,
  screenCreate: null,
  screenFilterOptions: {
    groups: {
      items: [],
      label: 'Groups',
      model: 'group_name',
    },
    ad_schedule: {
      items: [],
      label: 'Ad Schedule',
      model: 'ad_schedule',
    },
    area: {
      items: [],
      label: 'Area',
      model: 'area',
    },
    version: {
      items: [],
      label: 'Version',
      model: 'version',
    },
    unit_status: {
      items: ['Checked In', 'Checked Out', 'Reported'],
      label: 'Status',
      model: 'session_status',
    },
    trip_type: {
      items: [],
      label: 'Trip Type',
      model: 'trip_type',
    },
    network_status: {
      items: ['Green', 'Yellow', 'Red'],
      label: 'Green/Yellow/Red',
      model: 'network_status',
    },
    active_status: {
      items: ['Green', 'Yellow', 'Red'],
      label: 'Green/Yellow/Red',
      model: 'active_status',
    },
  },
  defaultNewScreen: {
    version: '',
    phone_number: '',
    charged_off: false,
    notify_users: false,
    screen_uptime: 0,
    uptime: false,
    debug: true,
    beep: false,
    device_id: 2394234,
    last_activity: 0,
    status: 'green',
    session_status: 'Checked In',
    trip_type: 'ROUND_TRIP',
    session_type: '',
  },
  queryParams: {
    page: 1,
    search: '',
  },
  lastEvalKey: 1,
};

// getters
const getters = {
  getAllScreens(state) {
    return state.allScreens;
  },
  getScreenFilterOptions(state) {
    return state.screenFilterOptions;
  },
  getQueryParams(state) {
    return state.queryParams;
  },
  getEditScreen(state) {
    return state.editScreen;
  },
  getNewScreen(state) {
    return state.newScreen;
  },
  getDefaultNewScreen(state) {
    return state.defaultNewScreen;
  },
  getTokens(state) {
    return state.tokens;
  },
  getCurrentTokenIndex(state) {
    return state.currentTokenIndex;
  },
  getScreenCreate(state) {
    return state.screenCreate;
  },
  getLastEvalKey(state) {
    return state.lastEvalKey;
  },
  getLoadingStates(state) {
    return state.loadingStates;
  },
};

// actions
const actions = {
  getAllScreensData: async (
    {
      commit, dispatch, rootGetters, getters
    },
    payload
  ) => new Promise((resolve, reject) => {
    commit('setLoadingState', { name: 'screensLoaded', value: true });

    let activeColor = null;

    const filters = JSON.parse(
      JSON.stringify(rootGetters['filters/getFilter'])
    );
    const userGroups =
        rootGetters['account/getCurrentUser'].group_id &&
        rootGetters['account/getCurrentUser'].group_id[0];
    let variables = {};

    let search = '';

    if (filters.search) {
      search = filters.search;
    }

    if (filters.launch_mode) {
      switch (filters.launch_mode) {
        case 'Bluetooth':
          filters.launch_mode = 'interface_select_bt';
          break;
        case 'Select':
          filters.launch_mode = 'interface_select';
          break;
        case 'Select+':
          filters.launch_mode = 'interface_select_plus';
          break;
        case 'Select++':
          filters.launch_mode = 'interface_select_plus_plus';
          break;
        case 'Prime':
          filters.launch_mode = 'interface_prime';
          break;
        default:
          break;
      }
    }

    if (userGroups && userGroups !== 'all') {
      variables.group_id = userGroups;
    } else if (filters.group_id !== undefined && filters.group_id !== '') {
      variables.group_id = filters.group_id;
    }
    if (filters.area !== undefined && filters.area !== '') {
      variables.area_id = filters.area;
    }
    if (filters.version !== undefined && filters.version !== '') {
      variables.version = filters.version;
    }
    if (filters.launch_mode !== undefined && filters.launch_mode !== '') {
      variables.launch_mode = filters.launch_mode;
    }
    if (filters.network_status) {
      variables.network_status = filters.network_status;
    }
    if (router.currentRoute.query.networkHealthFilter) {
      variables.network_status = router.currentRoute.query.networkHealthFilter;
    }
    if (router.currentRoute.query.networkHealthFilterGroup) {
      variables.group_id = router.currentRoute.query.networkHealthFilterGroup;
    }
    variables = JSON.stringify(variables).replace(/"([^"]+)":/g, '$1: ');

    if (filters.color !== '' && filters.color !== undefined) {
      activeColor = filters.color;
    }

    if (rootGetters['account/getCurrentClient'].client_name !== 'shark') {
      createPaginatedRequest(
        filterParams,
        callString(),
        'screen',
        payload.paginate,
      )
        .then((res) => {
          const screens = res.data.data.getPaginated ? res.data.data.getPaginated.items : [];

          dispatch('getScreenInfo', {
            data: screens,
            activeColor,
          });

          resolve(res);
        })
        .catch(err => {
          commit('loading/setLoading', false, { root: true });
          reject(err);
        });
    } else {
      let page;
      if (payload.paginate) {
        page = getters.getLastEvalKey + 1;
        commit('setLastEvalKey', page);
      } else {
        page = getters.getLastEvalKey;
      }
      const orderBy = payload.sortBy ? payload.sortBy : 'last_activity';
      const sortDir =
          payload.sortDesc && payload.sortDesc[0] === true ? 'ASC' : 'DESC';
      graphQL({
        query: `
            query {
              getAllScreens(
                itemsPerPage: ${payload.itemsPerPage}
                page: ${payload.page}
                search: "${search}"
                sortBy: "${orderBy}"
                sortDir: "${sortDir}"
                filter: ${variables}
              ) ${callString()}
            }
          `,
      })
        .then(res => {
          const screens = res.data.data.getAllScreens;
          if (router.currentRoute.query.networkHealthFilter) {
            router.replace({ query: null });
          }
          dispatch('getScreenInfo', {
            data: screens,
            activeColor,
          });
          resolve(res);
        })
        .catch(err => {
          commit('loading/setLoading', false, { root: true });
          reject(err);
        });
    }
  }),
  getScreenInfo({ commit, rootGetters, rootState }, payload) {
    return new Promise((resolve, reject) => {
      const clientTable = rootGetters['account/getCurrentClient'].table_name;
      getAllScreenItems(clientTable)
        .then(response => {
          setFilterOptions(payload);
          commit('setAllScreens', {
            data: payload.data,
            rounds: payload.rounds,
            rootState,
            screensInfo: response.data.Items,
            activeColor: payload.activeColor,
            client: rootGetters['account/getCurrentClient'].client_name,
          });
          commit('setLoadingState', { name: 'screensLoaded', value: false });
          resolve(response);
        })
        .catch(error => {
          commit('loading/setLoading', false, { root: true });
          reject(error);
        });
    });
  },
  addScreen: async ({ dispatch, commit, rootGetters }, payload) => new Promise((resolve, reject) => {
    payload = stringyObject(payload);
    const clientTable = rootGetters['account/getCurrentClient'].table_name;
    getAll(
      `/screen?query=${encodeURIComponent(
        `mutation{create(type_name: "screen", 
            screen: ${payload}, 
            table_name:"${clientTable}", 
          ){
            uuid, 
            ad_schedule, 
            beep, 
            charged_off, 
            area_id, 
            group_id, 
            store_id, 
            debug, 
            device_id, 
            notify_users, 
            phone_number, 
            screen_uptime, 
            session_status, 
            status, 
            version, 
            trip_type, 
            session_type, 
            checked, 
            pushy_token, 
            twilio, 
            last_activity,  
            error, 
            success
          }}`
      )}`
    )
      .then(res => {
        commit('setNewScreen', false);
        commit('setScreenCreate', 'New Screen Created');
        dispatch('getAllScreensData');
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  }),
};

// mutations
const mutations = {
  setLoadingState(state, payload) {
    state.loadingStates[payload.name] = payload.value;
  },
  setAllScreens(state, payload) {
    const allScreens = [];
    const nullScreens = [];

    payload.data.map(screen => {
      screen.round = null;
      payload.screensInfo.map(info => {
        if (info.uuid === screen.uuid) {
          screen.status = info.status;
        }
      });
      screen.id = screen.uuid;
      screen.type = 'screens';

      switch (screen.unit_status) {
        case 1:
          screen.unit_status = 'Checked In';
          screen.overdue_current = 'Current';
          break;
        case 2:
          screen.unit_status = 'Checked Out';
          screen.overdue_current = 'Current';
          break;
        case 3:
          screen.unit_status = 'Overdue';
          screen.overdue_current = 'Overdue';
          break;
        case 4:
          screen.unit_status = 'Reported';
          screen.overdue_current = 'Current';
          break;
        default:
          break;
      }

      if (screen.last_activity != null) {
        if (typeof screen.last_activity !== 'string') {
          screen.last_activity = moment(
            Number(screen.last_activity) * 1000
          ).format();
        }
        allScreens.push(screen);
      } else {
        nullScreens.push(screen);
      }
    });

    for (let i = 0; i < payload.data.length; i++) {
      if (payload.data[i].last_activity === null) {
        payload.data[i].last_activity = 'undefined';
      }
    }
    allScreens.sort((a, b) => b.last_activity.localeCompare(a.last_activity));
    if (payload.activeColor) {
      payload.data = payload.data.filter(
        screen => screen.status === payload.activeColor.toLowerCase()
      );
    }
    nullScreens.map(screen => {
      allScreens.push(screen);
    });

    if (state.createdScreen) {
      payload.data.unshift(state.createdScreen);
      state.createdScreen = null;
    }
    if (payload.client === 'shark') {
      Vue.set(state, 'allScreens', payload.data);
    } else {
      Vue.set(state, 'allScreens', allScreens);
    }
  },
  setFilteredScreens(state, payload) {
    state.allScreens = payload;
  },
  applyNewScreen(state, payload) {
    state.createdScreen = payload;
  },
  showAllScreens(state, payload) {
    payload.data.map(screen => {
      screen.show = true;
    });
    state.allScreens = payload;
  },
  setActiveStatus(state, payload) {
    const screen = state.allScreens.find(screen => screen.id === payload.id);
    screen.active_status = payload.color;
  },
  setQueryParams(state, payload) {
    state.queryParams = payload;
  },
  setEditScreen(state, payload) {
    state.editScreen = payload;
  },
  setNewScreen(state, payload) {
    state.newScreen = payload;
  },
  setScreenCreate(state, payload) {
    state.screenCreate = payload;
  },
  setTokens(state, payload) {
    if (!state.tokens.includes(payload)) {
      state.tokens.push(payload);
    }
  },
  setCurrentTokenIndex(state, payload) {
    state.currentTokenIndex = payload;
  },
  appendScreenGroupOptions(state, payload) {
    state.screenFilterOptions.groups.items.push({
      id: payload.uuid,
      name: payload.group_name,
    });
  },
  setScreenGroupOptions(state, payload) {
    state.screenFilterOptions.groups.items = payload;
  },
  setLastEvalKey(state, payload) {
    state.lastEvalKey = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
